import React from 'react'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { BankPaymentFormValues } from './BankPaymentForm.interface'
import { PaymentRecipientNameChoice } from './BankPaymentForm.constants'

const recipientNameChoiceLabelId = 'payment-recipient-name-choice-label'

interface PaymentRecipientNameChoiceGroupProps {
  personalName: string
  businessName: string
  sectionLabel: string
  dataTestId?: string
}

export const PaymentRecipientNameChoiceGroup = ({
  personalName,
  businessName,
  sectionLabel,
  dataTestId,
}: PaymentRecipientNameChoiceGroupProps) => {
  const { values, handleChange } = useFormikContext<BankPaymentFormValues>()

  const selected = values.paymentRecipientNameChoice

  const getTileStyles = (choice: PaymentRecipientNameChoice) => {
    const isSelected = selected === choice

    return {
      container: isSelected
        ? tw`rounded-lg border border-solid border-primary-500 bg-primary-25 py-3.5 pr-3 pl-0`
        : tw`rounded-lg border border-solid border-shades-400 bg-white py-3.5 pr-3 pl-0`,
      subtitle: isSelected ? tw`text-primary-500` : tw`text-shades-400`,
      radio: isSelected ? tw`text-primary-500` : tw`text-shades-400`,
    }
  }

  return (
    <FormControl css={tw`w-full`}>
      <FormLabel id={recipientNameChoiceLabelId}>
        <Typography css={tw`mb-2 text-shades-400 font-medium`} variant="h6">
          {sectionLabel}
        </Typography>
      </FormLabel>
      <RadioGroup
        aria-labelledby={recipientNameChoiceLabelId}
        name="paymentRecipientNameChoice"
        value={selected}
        onChange={handleChange('paymentRecipientNameChoice')}
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <FormControlLabel
          value={PaymentRecipientNameChoice.Personal}
          css={[
            tw`w-full ml-0 mb-2 items-start`,
            getTileStyles(PaymentRecipientNameChoice.Personal).container,
          ]}
          control={<Radio css={getTileStyles(PaymentRecipientNameChoice.Personal).radio} />}
          label={
            <div css={tw`w-full`}>
              <Typography css={tw`block font-medium text-shades-1000`}>
                Use personal name
              </Typography>
              <Typography
                css={[
                  tw`block text-sm`,
                  getTileStyles(PaymentRecipientNameChoice.Personal).subtitle,
                ]}
              >
                {personalName || '—'}
              </Typography>
            </div>
          }
        />
        <FormControlLabel
          value={PaymentRecipientNameChoice.Business}
          css={[
            tw`w-full ml-0 items-start`,
            getTileStyles(PaymentRecipientNameChoice.Business).container,
          ]}
          control={<Radio css={getTileStyles(PaymentRecipientNameChoice.Business).radio} />}
          label={
            <div css={tw`w-full`}>
              <Typography css={tw`block font-medium text-shades-1000`}>
                Use business name
              </Typography>
              <Typography
                css={[
                  tw`block text-sm`,
                  getTileStyles(PaymentRecipientNameChoice.Business).subtitle,
                ]}
              >
                {businessName || '—'}
              </Typography>
            </div>
          }
        />
      </RadioGroup>
    </FormControl>
  )
}
